/* import __COLOCATED_TEMPLATE__ from './summary-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import {
  loadInsightsSummary,
  type InsightsSummaryResult,
  type LoadInsightsSummaryArgs,
} from 'embercom/components/reporting/automation/ai-insights/conversation-summary-data-resources';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Range from 'embercom/models/reporting/range';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from './conversation-explore-panel';
import { action } from '@ember/object';
import { convertMarkdownToHtml } from './summary-converter';

interface Signature {
  Args: {
    selectedJob: Job;
    attributeId: string;
    attributeValue: string;
    breakdownAttributeValue?: string;
    dateRange: Range;
    onShowConversationExplorer: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading?: string,
    ) => void;
    summaryType: SummaryTab;
  };
}

export default class SummaryCard extends Component<Signature> {
  @service declare appService: AppService;
  @service declare reportingChartService: ReportingChartService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  fetchSummaryTask = taskFor(this.loadSummary);

  summaryData = trackedTask(this, this.fetchSummaryTask, () => [
    this.processedFilters,
    this.args.dateRange,
    this.args.attributeId,
  ]);

  get attributeLabel(): string {
    let attributeValue = this.args.attributeValue;
    let attributeId = this.args.attributeId;
    if (this.args.breakdownAttributeValue) {
      if (
        this.args.selectedJob.breakdownByAttributeId ===
        'conversation_custom_fields#ai_insights_topic_subtopic'
      ) {
        let [topic, subtopic] = this.args.breakdownAttributeValue.split('=>');
        return `${topic} -> ${subtopic}`;
      } else if (
        this.args.selectedJob.breakdownByAttributeId ===
        'conversation_custom_fields#ai_insights_topic'
      ) {
        return this.args.breakdownAttributeValue;
      } else {
        attributeValue = this.args.breakdownAttributeValue;
        attributeId = this.args.selectedJob.breakdownByAttributeId || '';
      }
    }

    let value = this.reportingUnderlyingDataService.mappedValueFor(attributeValue, attributeId);

    if (typeof value === 'string') {
      return value;
    } else if (value && typeof value === 'object') {
      return value.toString();
    }

    return this.args.attributeValue || '';
  }

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get processedFilters() {
    let filter = {
      type: 'category',
      data: {
        property: this.args.attributeId,
        attribute: this.args.attributeId,
        values: [this.args.attributeValue],
      },
    };
    if (this.args.selectedJob.chartSeries[0].filters) {
      return and(this.args.selectedJob.chartSeries[0].filters, filter, this.breakdownFilter);
    } else {
      return and(filter, this.breakdownFilter);
    }
  }

  get breakdownFilter() {
    if (!this.args.breakdownAttributeValue) {
      return undefined;
    }
    if (
      this.args.selectedJob.breakdownByAttributeId ===
      'conversation_custom_fields#ai_insights_topic_subtopic'
    ) {
      return {
        type: 'category',
        data: {
          property: 'conversation_custom_fields#ai_insights_subtopic',
          attribute: 'conversation_custom_fields#ai_insights_subtopic',
          values: [this.args.breakdownAttributeValue.split('=>')[1]],
        },
      };
    } else {
      return {
        type: 'category',
        data: {
          property: this.args.selectedJob.breakdownByAttributeId,
          attribute: this.args.selectedJob.breakdownByAttributeId,
          values: [this.args.breakdownAttributeValue],
        },
      };
    }
  }

  get summaryText(): string {
    return this.fetchSummaryTask.lastSuccessful?.value?.summaryText || '';
  }

  get summaryHeading(): string | undefined {
    if (this.args.summaryType !== 'resolution_rate_drivers') {
      return;
    }

    return `${this.args.attributeValue}: ${this.args.breakdownAttributeValue}`;
  }

  get processedSummaryText(): string {
    return convertMarkdownToHtml(this.summaryText, false, false);
  }

  @restartableTask
  *loadSummary(): TaskGenerator<InsightsSummaryResult | null> {
    let params = {
      appId: this.appService.app.id,
      adminId: this.appService.app.currentAdmin.id,
      filter: this.processedFilters,
      timeRange: this.timeRange,
      sortByAttributeId: 'conversation.first_user_conversation_part_created_at',
      sortDirection: 'desc',
      syntheticDataUseCase: this.reportingChartService.syntheticDataUseCase,
      timezone: this.appService.app.timezone,
      summaryType: this.args.summaryType,
      selectedCxAttribute: this.args.attributeValue,
      maxConversationCount: 5000,
    } as LoadInsightsSummaryArgs;

    if (this.args.summaryType === 'resolution_rate_drivers') {
      params.escalationReason = this.args.attributeValue;
      params.topicName = this.args.breakdownAttributeValue;
    }

    return yield loadInsightsSummary(params);
  }

  handleViewConversations = () => {
    this.args.onShowConversationExplorer(
      null,
      this.processedFilters,
      [this.args.attributeValue],
      'attribute_drivers',
      this.summaryText,
      this.summaryHeading,
    );
  };

  @action
  onConversationLinkClick(conversationId: number) {
    this.args.onShowConversationExplorer(
      conversationId,
      this.processedFilters,
      [this.args.attributeValue],
      'attribute_drivers',
      this.summaryText,
      this.summaryHeading,
    );
  }

  @action
  onSummaryTextClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.tagName === 'A' && target.dataset.conversationId) {
      event.preventDefault();
      let conversationId = parseInt(target.dataset.conversationId, 10);
      this.onConversationLinkClick(conversationId);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::SummaryCard': typeof SummaryCard;
    'reporting/automation/ai-insights/optimize/summary-card': typeof SummaryCard;
  }
}
